import React, { useState } from 'react';

import { Confirm, Icons, Modal } from '@components/common';
import Table from '@components/common/Table';
import ErrorCodesEditForm from '@components/ModalComponents/ErrorCodesEditForm';
import { TErrorCodesFormSubmitHandler } from '@components/ModalComponents/ErrorCodesEditForm/ErrorCodesEditForm';

import { useManageErrorCodes } from '@hooks/useManageErrorCodes';

import { errors as errorCodesSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setPage, setPageSize, setSearchValue, setSort } from '@slices/errors';

import {
	ISortBy,
	ITablePagination,
	ITableSearch, ITableSort,
} from '@interfaces/components/table';
import { IErrorCode } from '@interfaces/errors/apiErrors';

import { MODAL_WIDTH_480, MODAL_WIDTH_580, modalCloseConfirm } from '@constants/modals';
import { searchPlaceholderText, searchTooltipText } from './constants';

import { getColumns } from './manageErrorCodesColumns';

import styles from './ManageErrorCodes.module.scss';

const ManageErrorCodes = () => {
	const dispatch = useAppDispatch();
	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	} = useAppSelector(errorCodesSelector);

	const [isShowErrorCodesFormModal, setIsShowErrorCodesFormModal] = useState(false);
	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);

	const [editableErrorCode, setEditableErrorCode] = useState<IErrorCode | null>(null);

	const toggleShowErrorCodesFormModal = () => setIsShowErrorCodesFormModal(prevState => !prevState);
	const toggleCloseConfirmModal = () => {
		if (isDirtyFormModal) setIsCloseConfirmModal(prevState => !prevState);
		else closeModals();
	};

	const closeModals = () => {
		setIsShowErrorCodesFormModal(false);
		setIsCloseConfirmModal(false);
	};

	const {
		errorsByFilter,
		handleUpdateErrorCode,
		isLoadingErrorsByFilter,
		countOfRecords,
	} = useManageErrorCodes({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		closeModals,
	});

	const handleSort = (sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	};

	const onSearchHandler = (searchValue: string) => dispatch(setSearchValue({ searchValue }));

	const pageHandler = (page: number) => dispatch(setPage({ page }));

	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const search: Array<ITableSearch> = [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			placeholder: searchPlaceholderText,
			tooltipText: searchTooltipText,

		},
	];

	const columns = getColumns({
		setEditableErrorCode,
		toggleShowErrorCodesFormModal,
	});

	return (
		<div className={styles.errorCodesContainer}>
			{editableErrorCode && (
				<Modal
					isOpen={isShowErrorCodesFormModal}
					onClose={toggleCloseConfirmModal}
					title='Edit error code'
					width={MODAL_WIDTH_580}
				>
					<ErrorCodesEditForm
						editableErrorCode={editableErrorCode}
						onClose={toggleCloseConfirmModal}
						setIsDirtyFormModal={setIsDirtyFormModal}
						submitHandler={handleUpdateErrorCode as unknown as TErrorCodesFormSubmitHandler}
					/>
				</Modal>
			)}
			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleCloseConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={toggleCloseConfirmModal}
					{...modalCloseConfirm}
				/>
			</Modal>
			<div className={styles.pageTitle}>
				<div className={styles.icon}>{Icons.errorPlateActiveIcon({ isDisabled: true })}</div>
				<div className={styles.title}>Manage Error Codes</div>
			</div>
			<div className={styles.organisationsTableContainer}>
				<Table
					columns={columns}
					data={errorsByFilter}
					isLoading={isLoadingErrorsByFilter}
					isWithoutCheckbox
					isWithoutDeleteAction
					isWithoutFilter
					pagination={pagination}
					search={search}
					sort={sort}
				/>
			</div>
		</div>
	);
};

export default ManageErrorCodes;
